import { useState, useEffect, useRef } from "react";
import { Card, Table, Row, Col, Button, Tooltip, Empty, message } from "antd";
import { EditTwoTone, DeleteTwoTone, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import CustomPagination from '../../../components/CustomPagination';
import SingleSelectDropdownModal from "../../../components/InteractiveMap/SingleSelectDropdownModal/SingleSelectDropdownModal";
import TagsModal from "../../../components/InteractiveMap/TagsModal/TagsModal";
import "./data-management.css";
import {
  fetchAllProducts,
  fetchPartnerProducts,
  fetchProductPartners,
  submitExclusionData,
  getCountryList,
  updateERPApproval,
  fetchExclusionData,
  fetchAllActiveProductsData,
  updateDisplayFlag
} from "../../../appRedux/reducers/InteractiveMapsSlice";
import MultiSelectModal from "../../../components/InteractiveMap/MultiSelectModal/MultiSelectModal";
import EditPanelModal from "../../../components/InteractiveMap/EditPanelModal/EditPanelModal";
import ConfirmationPopup from "../../../components/ConfirmationPopup";

const getFlagEmoji = (countryIso) => {
  if (!countryIso) return "";
  return countryIso
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(127397 + char.charCodeAt())
    );
};

const DataManagement = () => {
  const dispatch = useDispatch();
  const erpFlagStatus = useSelector((state) => state.interactiveMaps.ERPFlagStatus);
  const exclusionData = useSelector(state => state.interactiveMaps.ExclusionConfig);
  const { ActiveProducts, isLoaded } = useSelector((state) => state.interactiveMaps);
  const highIncomeCountries = exclusionData?.exclude_high_income_county || [];
  const excludedProducts = exclusionData?.exclude_product || [];
  const excludedParaProducts = exclusionData?.para_product || [];
  const hideProductCountries = exclusionData?.hide_product_country || {};

  const [allCompanyProducts, setAllCompanyProducts] = useState([]);
  const [erpApproved, setErpApproved] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [erpModalVisible, setErpModalVisible] = useState(false);
  const [displayModalVisible, setDisplayModalVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [showCountriesPanel, setShowCountriesPanel] = useState(false);
  const [showProductsPanel, setShowProductsPanel] = useState(false);
  const [showParaProductsPanel, setShowParaProductsPanel] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedGlobalProducts, setSelectedGlobalProducts] = useState([]);
  const [selectedGlobalParaProducts, setSelectedGlobalParaProducts] = useState([]);

  // State for company exclusions
  const [companyExclusions, setCompanyExclusions] = useState([]);
  const [showCompanyExclusionsModal, setShowCompanyExclusionsModal] = useState(false);
  const [productPartners, setProductPartners] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [fetchingPartners, setFetchingPartners] = useState(false);

  // State for country exclusions
  const [countryExclusions, setCountryExclusions] = useState([]);
  const [showCountryExclusionsModal, setShowCountryExclusionsModal] = useState(false);
  const [selectedCountryExclusions, setSelectedCountryExclusions] = useState([]);
  const [fetchingCountries, setFetchingCountries] = useState(false);

  // Para Product Mapping
  const [paraProductMappings, setParaProductMappings] = useState([]);
  const [showParaMappingModal, setShowParaMappingModal] = useState(false);
  const [selectedParaProduct, setSelectedParaProduct] = useState(null);
  const [selectedMappedProduct, setSelectedMappedProduct] = useState(null);

  // Tag States for Company and Country
  const [currentCompanies, setCurrentCompanies] = useState([]);
  const [currentCountries, setCurrentCountries] = useState([]);
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showCountryTagsModal, setShowCountryTagsModal] = useState(false);

  // Add New Exclude Product for Specific Country
  const [isAddingCountryExclusion, setIsAddingCountryExclusion] = useState(false);
  const [selectedProductForAdd, setSelectedProductForAdd] = useState(null);

  // Add New Exclude Product for Specific Company
  const [isAddingCompanyExclusion, setIsAddingCompanyExclusion] = useState(false);
  const [selectedProductForCompanyAdd, setSelectedProductForCompanyAdd] = useState(null);

  // Add New Para Product Mapping
  const [isAddingParaMapping, setIsAddingParaMapping] = useState(false);
  const [selectedProductForParaMapping, setSelectedProductForParaMapping] = useState(null);
  const [selectedParaProductName, setSelectedParaProductName] = useState("");

  // Deletion Confirmation Popup States
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(''); // 'country', 'company', 'para'

  // Pagination states
  const [countryPagination, setCountryPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [allProductsPagination, setAllProductsPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [paraProductsPagination, setParaProductsPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [erpApprovedPagination, setErpApprovedPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [companyExclusionsPagination, setCompanyExclusionsPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [countryExclusionsPagination, setCountryExclusionsPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [paraMappingsPagination, setParaMappingsPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const [displayProductsPagination, setDisplayProductsPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Sorting states
  const [countrySortConfig, setCountrySortConfig] = useState({ field: null, direction: null });
  const [allProductsSortConfig, setAllProductsSortConfig] = useState({ field: null, direction: null });
  const [paraProductsSortConfig, setParaProductsSortConfig] = useState({ field: null, direction: null });
  const [erpApprovedSortConfig, setErpApprovedSortConfig] = useState({ field: null, direction: null });
  const [companyExclusionsSortConfig, setCompanyExclusionsSortConfig] = useState({ field: null, direction: null });
  const [countryExclusionsSortConfig, setCountryExclusionsSortConfig] = useState({ field: null, direction: null });
  const [paraMappingsSortConfig, setParaMappingsSortConfig] = useState({ field: null, direction: null });
  const [displayProductsSortConfig, setDisplayProductsSortConfig] = useState({ field: null, direction: null });

  const [currentCountryRecordKey, setCurrentCountryRecordKey] = useState(null);

  // Add sorting functions
  const stringSorter = (a, b, key) => {
    const aValue = a[key] || '';
    const bValue = b[key] || '';
    return aValue.localeCompare(bValue);
  };

  const countrySorter = (a, b) => {
    const aValue = a.name || '';
    const bValue = b.name || '';
    return aValue.localeCompare(bValue);
  };

  // Helper function to sort data based on sort config
  const getSortedData = (data, sortConfig) => {
    if (!sortConfig.field || !sortConfig.direction) {
      return data;
    }

    const sortedData = [...data].sort((a, b) => {
      let comparison = 0;
      
      if (sortConfig.field === 'name') {
        comparison = stringSorter(a, b, 'name');
      } else if (sortConfig.field === 'product_name') {
        // Handle both direct product_name and nested product_id.product_name
        const aValue = a.product_name || a.product_id?.product_name || '';
        const bValue = b.product_name || b.product_id?.product_name || '';
        comparison = aValue.localeCompare(bValue);
      } else if (sortConfig.field === 'company_name') {
        const aValue = a.partner_id?.company_name || '';
        const bValue = b.partner_id?.company_name || '';
        comparison = aValue.localeCompare(bValue);
      } else if (sortConfig.field === 'country_name') {
        comparison = countrySorter(a, b);
      } else {
        // Generic string sorting for other fields
        comparison = stringSorter(a, b, sortConfig.field);
      }

      return sortConfig.direction === 'descend' ? -comparison : comparison;
    });

    return sortedData;
  };

  // Handle table change (sorting, pagination)
  const handleTableChange = (pagination, filters, sorter, setSortConfig, setPagination) => {
    // Update sort configuration
    if (sorter && sorter.field) {
      setSortConfig({
        field: sorter.field,
        direction: sorter.order
      });
    } else {
      setSortConfig({ field: null, direction: null });
    }

    // Reset to first page when sorting changes
    setPagination(prev => ({
      ...prev,
      current: sorter && sorter.field ? 1 : pagination.current || prev.current
    }));
  };


  // Fetch exclusion data on mount
  useEffect(() => {
    dispatch(fetchExclusionData());
    dispatch(fetchAllActiveProductsData());
  }, [dispatch]);

  // Initialize company exclusions from exclusion data
  useEffect(() => {
    if (exclusionData?.exclude_product_partner) {
      setCompanyExclusions(exclusionData.exclude_product_partner);
    }
  }, [exclusionData]);

  // Add with other useEffect hooks
  useEffect(() => {
    if (exclusionData?.map_para_product) {
      setParaProductMappings(exclusionData.map_para_product);
    }
  }, [exclusionData]);

  // Initialize country exclusions from exclusion data
  useEffect(() => {
    if (hideProductCountries) {
      const formattedExclusions = Object.entries(hideProductCountries).map(([productName, countries]) => {
        const product = allCompanyProducts.find(p => p.name === productName);
        return {
          product_id: product?.id || null,
          product_name: productName,
          country_list: countries
        };
      }).filter(item => item.product_id !== null);
      
      setCountryExclusions(formattedExclusions);
    }
  }, [hideProductCountries, allCompanyProducts]);

  // Initialize selected countries from exclusion data
  useEffect(() => {
    if (highIncomeCountries.length > 0 && countryList.length > 0) {
      const selected = countryList.filter(country => 
        highIncomeCountries.includes(country.iso)
      );
      setSelectedCountries(selected);
    }
  }, [highIncomeCountries, countryList]);

  // Initialize selected products from exclusion data
  useEffect(() => {
    if (excludedProducts.length > 0 && allCompanyProducts.length > 0) {
      const selected = allCompanyProducts.filter(product => 
        excludedProducts.includes(product.name)
      );
      setSelectedGlobalProducts(selected);
    }
  }, [excludedProducts, allCompanyProducts]);

  // Initialize selected para products from exclusion data
  useEffect(() => {
    if (excludedParaProducts?.length > 0 && allCompanyProducts.length > 0) {
      const selected = allCompanyProducts.filter(product => 
        excludedParaProducts.includes(product.name)
      );
      setSelectedGlobalParaProducts(selected);
    }
  }, [excludedParaProducts, allCompanyProducts]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch all products
      const allProductsRes = await dispatch(fetchAllProducts());
      const allProducts = unwrapResult(allProductsRes).results || [];
      
      setAllCompanyProducts(
        allProducts.map((item) => ({
          id: item.product_id,
          name: item.product_name,
        }))
      );

      // Set ERP approved status
      setErpApproved(allProducts.map((item) => ({
        key: item.product_id,
        name: item.product_name,
        is_erp_approved: item.is_erp_approved,
        status: item.is_erp_approved ? "Approved" : "Not Approved",
      })));

      // Fetch country list
      const countryListRes = await dispatch(getCountryList());
      const countries = unwrapResult(countryListRes);
      setCountryList(countries.map(country => ({
        id: country.country_id,
        name: country.country_name,
        iso: country.country_iso,
      })));

    } catch (error) {
      console.error("API error:", error);
      message.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dispatch]);

  const prevErpFlagStatusRef = useRef();
  useEffect(() => {
    if (erpFlagStatus === "success" && prevErpFlagStatusRef.current !== "success") {
      fetchData();
    }
    prevErpFlagStatusRef.current = erpFlagStatus;
  }, [erpFlagStatus]);

  const handleERPStatusUpdate = async (value) => {
    try {
      if (selectedProduct?.key) {
        await dispatch(updateERPApproval({ productId: selectedProduct.key, flag: value }));
        setErpModalVisible(false);
        message.success("ERP approval status updated successfully");
        fetchData();
      }
    } catch (error) {
      message.error("Failed to update ERP approval status");
    }
  };

  function convertToNameArray(countryData) {
    return countryData.map(country => country.name);
  }

  const countriesOptionsList = convertToNameArray(countryList);

  const handleDisplayStatusUpdate = async (value) => {
    try {
      if (selectedProduct?.key) {
        await dispatch(updateDisplayFlag({
          activeProductId: selectedProduct.key,
          flag: value
        }));
        setDisplayModalVisible(false);
        message.success("Display status updated successfully");
        dispatch(fetchAllActiveProductsData());
      }
    } catch (error) {
      message.error("Failed to update display status");
    }
  };

  const handleSaveCountries = async (countriesToSave) => {
    try {
      setSaving(true);
      const countryCodes = countriesToSave.map(country => country.iso);

      const result = await dispatch(submitExclusionData({
        exclude_high_income_county: countryCodes
      }));

      unwrapResult(result);
      message.success("High income countries updated successfully");
      setSelectedCountries(countriesToSave);
      setShowCountriesPanel(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save high income countries:", error);
      message.error("Failed to update high income countries");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProducts = async (productsToSave) => {
    try {
      setSaving(true);
      const productNames = productsToSave.map(product => product.name);

      const result = await dispatch(submitExclusionData({
        exclude_product: productNames
      }));

      unwrapResult(result);
      message.success("Excluded products updated successfully");
      setSelectedGlobalProducts(productsToSave);
      setShowProductsPanel(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save excluded products:", error);
      message.error("Failed to update excluded products");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveParaProducts = async (productsToSave) => {
    try {
      setSaving(true);
      const productNames = productsToSave.map(product => product.name);

      const result = await dispatch(submitExclusionData({
        para_product: productNames
      }));

      unwrapResult(result);
      message.success("Excluded para products updated successfully");
      setSelectedGlobalParaProducts(productsToSave);
      setShowParaProductsPanel(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save excluded para products:", error);
      message.error("Failed to update excluded para products");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenCompanyExclusions = async (product) => {
    try {
      setSelectedProduct(product);
      setFetchingPartners(true);
      setShowCompanyExclusionsModal(true);
      
      // Fetch partners for this product
      const partnersRes = await dispatch(fetchProductPartners(product.product_id));
      const partners = unwrapResult(partnersRes);
      
      // Extract company names from partners
      const companyNames = partners.map(partner => partner.company_name);
      setProductPartners(companyNames);
      
      // Set currently selected companies from our maintained state
      const currentExclusion = companyExclusions.find(
        item => item.product_id === product.product_id
      );
      setSelectedCompanies(currentExclusion?.company_list || []);
    } catch (error) {
      message.error("Failed to fetch product partners");
      console.error(error);
    } finally {
      setFetchingPartners(false);
    }
  };

  const handleOpenCountryExclusions = async (product) => {
    try {
      setSelectedProduct(product);
      setFetchingCountries(true);
      setShowCountryExclusionsModal(true);
      
      // Set currently selected countries from our maintained state
      const currentExclusion = countryExclusions.find(
        item => item.product_id === product.product_id
      );
      setSelectedCountryExclusions(currentExclusion?.country_list || []);
    } catch (error) {
      message.error("Failed to fetch country exclusions");
      console.error(error);
    } finally {
      setFetchingCountries(false);
    }
  };

  const handleSaveCompanyExclusions = async () => {
    try {
      setSaving(true);
      
      // Create updated exclusions array
      let updatedExclusions = [...companyExclusions];
      
      // Find if this product already has exclusions
      const existingIndex = updatedExclusions.findIndex(
        item => item.product_id === selectedProduct.product_id
      );
      
      if (existingIndex >= 0) {
        // Update existing entry
        updatedExclusions[existingIndex] = {
          ...updatedExclusions[existingIndex],
          company_list: [...selectedCompanies]
        };
      } else {
        // Add new entry
        updatedExclusions.push({
          product_id: selectedProduct.product_id,
          product_name: selectedProduct.product_name,
          company_list: [...selectedCompanies]
        });
      }
      
      // Submit complete updated array to API
      const result = await dispatch(submitExclusionData({
        exclude_product_partner: updatedExclusions
      }));
      
      unwrapResult(result);
      message.success("Company exclusions updated successfully");
      setCompanyExclusions(updatedExclusions);
      setShowCompanyExclusionsModal(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save company exclusions:", error);
      message.error("Failed to update company exclusions");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCountryExclusions = async () => {
    try {
      setSaving(true);
      
      // Create updated exclusions object
      const updatedExclusions = { ...hideProductCountries };
      
      // Update the current product's exclusions (already in ISO format)
      updatedExclusions[selectedProduct.product_name] = [...selectedCountryExclusions];
      
      // Submit complete updated object to API
      const result = await dispatch(submitExclusionData({
        hide_product_country: updatedExclusions
      }));
      
      unwrapResult(result);
      message.success("Country exclusions updated successfully");
      
      // Update local state
      const updatedCountryExclusions = countryExclusions.filter(
        item => item.product_id !== selectedProduct.product_id
      );
      
      if (selectedCountryExclusions.length > 0) {
        updatedCountryExclusions.push({
          product_id: selectedProduct.product_id,
          product_name: selectedProduct.product_name,
          country_list: [...selectedCountryExclusions]
        });
      }
      
      setCountryExclusions(updatedCountryExclusions);
      setShowCountryExclusionsModal(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save country exclusions:", error);
      message.error("Failed to update country exclusions");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenParaMapping = (mapping) => {
  setSelectedParaProduct(mapping);
  setSelectedMappedProduct(allCompanyProducts.find(p => p.name === mapping.product_name) || null);
  setIsAddingParaMapping(false);
  setShowParaMappingModal(true);
};

  // Para Product Mapping Handler function
  const handleSaveParaMapping = async () => {
    try {
      setSaving(true);
      
      // Create updated mappings array
      let updatedMappings = [...paraProductMappings];
      
      // Find if this product already has a para product mapping
      const existingIndex = updatedMappings.findIndex(
        item => item.product_name === selectedMappedProduct?.name
      );
      
      if (existingIndex >= 0) {
        // Update existing entry
        updatedMappings[existingIndex] = {
          ...updatedMappings[existingIndex],
          para_name: selectedParaProduct?.para_name || ""
        };
      } else {
        // Add new entry
        updatedMappings.push({
          product_name: selectedMappedProduct?.name || "",
          para_name: selectedParaProduct?.para_name || ""
        });
      }
      
      // Submit complete updated array to API
      const result = await dispatch(submitExclusionData({
        map_para_product: updatedMappings
      }));
      
      unwrapResult(result);
      message.success("Para product mapping updated successfully");
      setParaProductMappings(updatedMappings);
      setShowParaMappingModal(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to save para product mapping:", error);
      message.error("Failed to update para product mapping");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddCountryExclusion = () => {
    setIsAddingCountryExclusion(true);
    setSelectedProductForAdd(null);
    setSelectedCountryExclusions([]);
    setShowCountryExclusionsModal(true);
  };

  const handleAddCountryExclusion = async () => {
    try {
      if (!selectedProductForAdd) {
        message.error("Please select a product");
        return;
      }

      setSaving(true);
      
      // Create updated exclusions object including the new one
      const updatedExclusions = { ...hideProductCountries };
      
      // Add the new product's exclusions
      updatedExclusions[selectedProductForAdd.name] = [...selectedCountryExclusions];
      
      // Submit complete updated object to API
      const result = await dispatch(submitExclusionData({
        hide_product_country: updatedExclusions
      }));
      
      unwrapResult(result);
      message.success("Country exclusion added successfully");
      
      // Update local state
      const updatedCountryExclusions = [...countryExclusions];
      
      if (selectedCountryExclusions.length > 0) {
        updatedCountryExclusions.push({
          product_id: selectedProductForAdd.id,
          product_name: selectedProductForAdd.name,
          country_list: [...selectedCountryExclusions]
        });
      }
      
      setCountryExclusions(updatedCountryExclusions);
      setShowCountryExclusionsModal(false);
      setIsAddingCountryExclusion(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to add country exclusion:", error);
      message.error("Failed to add country exclusion");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddCompanyExclusion = () => {
    setIsAddingCompanyExclusion(true);
    setSelectedProductForCompanyAdd(null);
    setSelectedCompanies([]);
    setProductPartners([]); // Reset partners list
    setShowCompanyExclusionsModal(true);
  };

  const handleAddCompanyExclusion = async () => {
    try {
      if (!selectedProductForCompanyAdd) {
        message.error("Please select a product");
        return;
      }

      setSaving(true);
      
      // Create updated exclusions array including the new one
      let updatedExclusions = [...companyExclusions];
      
      // Add the new product's exclusions
      updatedExclusions.push({
        product_id: selectedProductForCompanyAdd.id,
        product_name: selectedProductForCompanyAdd.name,
        company_list: [...selectedCompanies]
      });
      
      // Submit complete updated array to API
      const result = await dispatch(submitExclusionData({
        exclude_product_partner: updatedExclusions
      }));
      
      unwrapResult(result);
      message.success("Company exclusion added successfully");
      setCompanyExclusions(updatedExclusions);
      setShowCompanyExclusionsModal(false);
      setIsAddingCompanyExclusion(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to add company exclusion:", error);
      message.error("Failed to add company exclusion");
    } finally {
      setSaving(false);
    }
  };

  const handleProductChangeForCompanyAdd = async (productId) => {
    try {
      setFetchingPartners(true);
      const product = allCompanyProducts.find(p => p.id === productId);
      setSelectedProductForCompanyAdd(product);
      
      // Fetch partners for the selected product
      const partnersRes = await dispatch(fetchProductPartners(productId));
      const partners = unwrapResult(partnersRes);
      
      // Extract company names from partners
      const companyNames = partners.map(partner => partner.company_name);
      setProductPartners(companyNames);
      setSelectedCompanies([]); // Reset selected companies when product changes
    } catch (error) {
      message.error("Failed to fetch product partners");
      console.error(error);
    } finally {
      setFetchingPartners(false);
    }
  };

  const handleSaveNewParaMapping = async () => {
    try {
      if (!selectedProductForParaMapping || !selectedParaProductName) {
        message.error("Please select both product and para product");
        return;
      }

      setSaving(true);
      
      // Create updated mappings array
      let updatedMappings = [...paraProductMappings];
      
      // Check if this product already has a mapping
      const existingIndex = updatedMappings.findIndex(
        item => item.product_name === selectedProductForParaMapping.name
      );
      
      if (existingIndex >= 0) {
        // Update existing entry
        updatedMappings[existingIndex] = {
          ...updatedMappings[existingIndex],
          para_name: selectedParaProductName
        };
      } else {
        // Add new entry
        updatedMappings.push({
          product_name: selectedProductForParaMapping.name,
          para_name: selectedParaProductName
        });
      }
      
      // Submit complete updated array to API
      const result = await dispatch(submitExclusionData({
        map_para_product: updatedMappings
      }));
      
      unwrapResult(result);
      message.success("Para product mapping added successfully");
      setParaProductMappings(updatedMappings);
      setShowParaMappingModal(false);
      setIsAddingParaMapping(false);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to add para product mapping:", error);
      message.error("Failed to add para product mapping");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (record, type) => {
    setRecordToDelete(record);
    setDeleteType(type);
    setDeleteConfirmationVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete || !deleteType) return;

    try {
      setSaving(true);
      
      switch (deleteType) {
        case 'country':
          await handleDeleteCountryExclusion(recordToDelete);
          break;
        case 'company':
          await handleDeleteCompanyExclusion(recordToDelete);
          break;
        case 'para':
          await handleDeleteParaMapping(recordToDelete);
          break;
        default:
          break;
      }
      
      message.success("Item deleted successfully");
    } catch (error) {
      console.error("Failed to delete item:", error);
      message.error("Failed to delete item");
    } finally {
      setSaving(false);
      setDeleteConfirmationVisible(false);
      setRecordToDelete(null);
      setDeleteType('');
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmationVisible(false);
    setRecordToDelete(null);
    setDeleteType('');
  };

  const handleDeleteCountryExclusion = async (record) => {
    try {
      setSaving(true);
      
      const updatedExclusions = { ...hideProductCountries };
      delete updatedExclusions[record.product_name];
      
      const result = await dispatch(submitExclusionData({
        hide_product_country: updatedExclusions
      }));
      
      unwrapResult(result);
      
      const updatedCountryExclusions = countryExclusions.filter(
        item => item.product_id !== record.product_id
      );
      
      // Check if current page becomes empty after deletion
      const { current, pageSize } = countryExclusionsPagination;
      const totalAfterDeletion = updatedCountryExclusions.length;
      const maxPage = Math.ceil(totalAfterDeletion / pageSize) || 1;
      
      if (current > maxPage) {
        setCountryExclusionsPagination(prev => ({
          ...prev,
          current: Math.max(1, maxPage)
        }));
      }
      
      setCountryExclusions(updatedCountryExclusions);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to delete country exclusion:", error);
      throw error; // Throw error to be caught by confirmation handler
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompanyExclusion = async (record) => {
    try {
      setSaving(true);
      
      const updatedExclusions = companyExclusions.filter(
        item => item.product_id !== record.product_id
      );
      
      const result = await dispatch(submitExclusionData({
        exclude_product_partner: updatedExclusions
      }));
      
      unwrapResult(result);
      
      // Check if current page becomes empty after deletion
      const { current, pageSize } = companyExclusionsPagination;
      const totalAfterDeletion = updatedExclusions.length;
      const maxPage = Math.ceil(totalAfterDeletion / pageSize) || 1;
      
      if (current > maxPage) {
        setCompanyExclusionsPagination(prev => ({
          ...prev,
          current: Math.max(1, maxPage)
        }));
      }
      
      setCompanyExclusions(updatedExclusions);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to delete company exclusion:", error);
      throw error; // Throw error to be caught by confirmation handler
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteParaMapping = async (record) => {
    try {
      setSaving(true);
      
      const updatedMappings = paraProductMappings.filter(
        item => item.product_name !== record.product_name
      );
      
      const result = await dispatch(submitExclusionData({
        map_para_product: updatedMappings
      }));
      
      unwrapResult(result);
      
      // Check if current page becomes empty after deletion
      const { current, pageSize } = paraMappingsPagination;
      const totalAfterDeletion = updatedMappings.length;
      const maxPage = Math.ceil(totalAfterDeletion / pageSize) || 1;
      
      if (current > maxPage) {
        setParaMappingsPagination(prev => ({
          ...prev,
          current: Math.max(1, maxPage)
        }));
      }
      
      setParaProductMappings(updatedMappings);
      dispatch(fetchExclusionData());
    } catch (error) {
      console.error("Failed to delete para product mapping:", error);
      throw error; // Throw error to be caught by confirmation handler
    } finally {
      setSaving(false);
    }
  };

  const countryRenderer = (country) => (
    <div className="country-info">
      {/* <span className="country-flag">{getFlagEmoji(country.iso)}</span> */}
      <span className="country-name">{country.name}</span>
    </div>
  );

  const productRenderer = (product) => (
    <div className="product-info">
      <span className="product-name">{product.name}</span>
    </div>
  );

  const columnsCompanyExclusions = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
      sortOrder: companyExclusionsSortConfig.field === 'product_name' ? companyExclusionsSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
    },
    {
      title: "Excluded Companies",
      dataIndex: "company_list",
      key: "company_list",
      render: (companies) => (
        <div className="company-tags-container">
          {companies?.slice(0, 2).map((company, index) => (
            <Button
              key={index}
              size="small"
              type="default"
              className="status-pill company-tag"
              style={{
                backgroundColor: "#F6F6F6",
                borderColor: "#d9d9d9",
                color: "#666",
                marginRight: 0,
                marginBottom: 0
              }}
            >
              {company}
            </Button>
          ))}
          {companies?.length > 2 && (
            <span className="more-button-container">
              <Button
                size="small"
                type="default"
                className="status-pill company-tag-more"
                style={{
                  color: "#00389A",
                  border:"none",
                  marginRight: 8,
                  marginBottom: 8
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentCompanies(companies);
                  setShowTagsModal(true);
                }}
              >
                +{companies?.length - 2} more
              </Button>
              {showTagsModal && currentCompanies === companies && (
                <TagsModal
                  visible={true}
                  companies={currentCompanies}
                  onClose={() => setShowTagsModal(false)}
                />
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit">
            <EditTwoTone 
              className="action-icon"
              twoToneColor={["#283a8a", "#fff"]}
              onClick={() => handleOpenCompanyExclusions(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteTwoTone 
              className="action-icon" 
              twoToneColor={["#F5212D", "#fff"]}
              onClick={() => handleDeleteClick(record, 'company')}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const columnsCountryExclusions = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
      sortOrder: countryExclusionsSortConfig.field === 'product_name' ? countryExclusionsSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
    },
    {
      title: "Excluded Countries",
      dataIndex: "country_list",
      key: "country_list",
      render: (countries, record) => (
        <div className="company-tags-container">
          {countries.slice(0, 2).map((countryCode, index) => {
            const country = countryList.find(c => c.iso === countryCode);
            return (
              <Button
                key={index}
                size="small"
                type="default"
                className="status-pill company-tag"
                style={{
                  backgroundColor: "#F6F6F6",
                  borderColor: "#d9d9d9",
                  color: "#666",
                  marginRight: 0,
                  marginBottom: 0
                }}
              >
                {country ? `${country.name}` : countryCode}
              </Button>
            );
          })}
          {countries.length > 2 && (
            <span className="more-button-container">
              <Button
                size="small"
                type="default"
                className="status-pill company-tag-more"
                style={{
                  color: "#00389A",
                  border:"none",
                  marginRight: 8,
                  marginBottom: 8
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  const countryNames = countries.map(code => {
                    const country = countryList.find(c => c.iso === code);
                    return country ? `${country.name}` : code;
                  });
                  setCurrentCountries(countryNames);
                  setCurrentCountryRecordKey(record.key || record.product_name); // Use a unique identifier
                  setShowCountryTagsModal(true);
                }}
              >
                +{countries.length - 2} more
              </Button>
              {showCountryTagsModal && currentCountryRecordKey === (record.key || record.product_name) && (
                <TagsModal
                  visible={true}
                  companies={currentCountries}
                  header="Country Names"
                  onClose={() => {
                    setShowCountryTagsModal(false);
                    setCurrentCountryRecordKey(null);
                  }}
                />
              )}
            </span>
          )}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit">
            <EditTwoTone 
              className="action-icon" 
              twoToneColor={["#283a8a", "#fff"]}
              onClick={() => handleOpenCountryExclusions(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteTwoTone 
              className="action-icon" 
              twoToneColor={["#F5212D", "#fff"]}
              onClick={() => handleDeleteClick(record, 'country')}
            />
          </Tooltip>
      </div>
      ),
    },
  ];

  const columnsERPApproved = [
    {
      title: "Product Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: erpApprovedSortConfig.field === 'name' ? erpApprovedSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => (
        <Button
          size="small"
          type={record.is_erp_approved ? "primary" : "default"}
          className="status-pill"
          style={{
            backgroundColor: record.is_erp_approved ? "#52c41a" : "#F6F6F6",
            borderColor: record.is_erp_approved ? "#52c41a" : "#d9d9d9",
            color: record.is_erp_approved ? "#fff" : "#666"
          }}
        >
          {status}
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <EditTwoTone 
              className="action-icon" 
              twoToneColor={["#283a8a", "#fff"]}
              onClick={() => {
                setSelectedProduct(record);
                setErpModalVisible(true);
              }}
            />
          </Tooltip>
        </>
      ),
    },
  ];

  const columnsDisplayProducts = [
    {
      title: "Company Name",
      dataIndex: "company_name",
      key: "company_name",
      sorter: (a, b) => a.partner_id.company_name.localeCompare(b.partner_id.company_name),
      sortOrder: displayProductsSortConfig.field === 'company_name' ? displayProductsSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      render: (_, record) => record.partner_id.company_name
    },
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: (a, b) => a.product_id.product_name.localeCompare(b.product_id.product_name),
      sortOrder: displayProductsSortConfig.field === 'product_name' ? displayProductsSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      render: (_, record) => record.product_id.product_name
    },
    {
      title: "Display on Website",
      dataIndex: "display_on_website",
      key: "display_on_website",
      width: 180,
      align: 'center',
      render: (status) => (
        <Button
          size="small"
          type={status ? "primary" : "default"}
          className="status-pill"
          style={{
            backgroundColor: status ? "#52c41a" : "#F6F6F6",
            borderColor: status ? "#52c41a" : "#d9d9d9",
            color: status ? "#fff" : "#666"
          }}
        >
          {status ? "Yes" : "No"}
        </Button>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <Tooltip title="Edit">
          <EditTwoTone 
            className="action-icon" 
            twoToneColor={["#283a8a", "#fff"]}
            onClick={() => {
              setSelectedProduct({
                key: record.active_product_id,
                name: record.product_id.product_name,
                company: record.partner_id.company_name,
                display_on_website: record.display_on_website
              });
              setDisplayModalVisible(true);
            }}
          />
        </Tooltip>
      ),
    },
  ];

  const columnsParaProductMapping = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      sorter: true,
      sortOrder: paraMappingsSortConfig.field === 'product_name' ? paraMappingsSortConfig.direction : null,
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
    },
    {
      title: "Para Product Name",
      dataIndex: "para_name",
      key: "para_name",
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      align: "center",
      render: (_, record) => (
        <div className="action-buttons">
          <Tooltip title="Edit">
            <EditTwoTone 
              className="action-icon"
              twoToneColor={["#283a8a", "#fff"]} 
              onClick={() => handleOpenParaMapping(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <DeleteTwoTone 
              className="action-icon" 
              twoToneColor={["#F5212D", "#fff"]}
              onClick={() => handleDeleteClick(record, 'para')}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
  <div className="data-management-wrapper">
    <h1 className="page-title">Interactive Map Data List</h1>
    <Row gutter={[16, 16]}>
      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Exclude High income Country</span>
              <Tooltip title="Edit">
              <Button 
                type="link" 
                icon={<EditOutlined />} 
                onClick={() => setShowCountriesPanel(true)}
              />
              </Tooltip>
            </div>
          } 
          className="company-products-card"
        >
          {selectedCountries.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(selectedCountries, countrySortConfig);
                  return sortedData.slice(
                    (countryPagination.current - 1) * countryPagination.pageSize,
                    countryPagination.current * countryPagination.pageSize
                  );
                })()}
                columns={[
                  {
                    title: "Country Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: true,
                    sortOrder: countrySortConfig.field === 'name' ? countrySortConfig.direction : null,
                    sortDirections: ['ascend', 'descend'],
                    showSorterTooltip: false,
                    render: (_, record) => {
                      // const flag = getFlagEmoji(record.iso);
                      return (
                        <span>
                          {/* <span className="flag-icon">{flag}</span>  */}
                          {record.name}
                        </span>
                      );
                    },
                  },
                ]}
                pagination={false}
                rowKey="id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setCountrySortConfig, setCountryPagination)
                }
              />
              <CustomPagination
                current={countryPagination.current}
                total={selectedCountries.length}
                pageSize={countryPagination.pageSize}
                onChange={(page, pageSize) => {
                  setCountryPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No countries selected yet"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Exclude Product For All Company</span>
              <Tooltip title="Edit">
              <Button 
                type="link" 
                icon={<EditOutlined />} 
                onClick={() => setShowProductsPanel(true)}
              />
              </Tooltip>
            </div>
          } 
          className="company-products-card"
        >
          {selectedGlobalProducts.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(selectedGlobalProducts, allProductsSortConfig);
                  return sortedData.slice(
                    (allProductsPagination.current - 1) * allProductsPagination.pageSize,
                    allProductsPagination.current * allProductsPagination.pageSize
                  );
                })()}
                columns={[
                  {
                    title: "Product Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: true,
                    sortOrder: allProductsSortConfig.field === 'name' ? allProductsSortConfig.direction : null,
                    sortDirections: ['ascend', 'descend'],
                    showSorterTooltip: false,
                  }
                ]}
                pagination={false}
                rowKey="id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setAllProductsSortConfig, setAllProductsPagination)
                }
              />
              <CustomPagination
                current={allProductsPagination.current}
                total={selectedGlobalProducts.length}
                pageSize={allProductsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setAllProductsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No products selected yet"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Exclude Product for Specific Country</span>
              <div>
                <Tooltip title="Add New">
                <Button 
                  type="link" 
                  icon={<PlusOutlined />} 
                  onClick={() => handleOpenAddCountryExclusion()}
                  style={{ marginRight: 8 }}
                />
                </Tooltip>
              </div>
            </div>
          }  
          className="company-products-card"
          loading={loading}
        >
          {countryExclusions?.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(countryExclusions, countryExclusionsSortConfig);
                  return sortedData.slice(
                    (countryExclusionsPagination.current - 1) * countryExclusionsPagination.pageSize,
                    countryExclusionsPagination.current * countryExclusionsPagination.pageSize
                  );
                })()}
                columns={columnsCountryExclusions}
                pagination={false}
                rowKey="product_id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setCountryExclusionsSortConfig, setCountryExclusionsPagination)
                }
              />
              <CustomPagination
                current={countryExclusionsPagination.current}
                total={countryExclusions.length}
                pageSize={countryExclusionsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setCountryExclusionsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No data available"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Exclude Product for Specific Company</span>
              <div>
              <Tooltip title="Add New">
                <Button 
                  type="link" 
                  icon={<PlusOutlined />} 
                  onClick={() => handleOpenAddCompanyExclusion()}
                  style={{ marginRight: 8 }}
                />
              </Tooltip>
              </div>
            </div>
          } 
          className="company-products-card"
          loading={loading}
        >
          {companyExclusions?.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(companyExclusions, companyExclusionsSortConfig);
                  return sortedData.slice(
                    (companyExclusionsPagination.current - 1) * companyExclusionsPagination.pageSize,
                    companyExclusionsPagination.current * companyExclusionsPagination.pageSize
                  );
                })()}
                columns={columnsCompanyExclusions}
                pagination={false}
                rowKey="product_id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setCompanyExclusionsSortConfig, setCompanyExclusionsPagination)
                }
              />
              <CustomPagination
                current={companyExclusionsPagination.current}
                total={companyExclusions.length}
                pageSize={companyExclusionsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setCompanyExclusionsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No data available"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Exclude Para Product For All Company</span>
              <Tooltip title="Edit">
              <Button 
                type="link" 
                icon={<EditOutlined />} 
                onClick={() => setShowParaProductsPanel(true)}
              />
              </Tooltip>
            </div>
          } 
          className="company-products-card"
        >
          {selectedGlobalParaProducts.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(selectedGlobalParaProducts, paraProductsSortConfig);
                  return sortedData.slice(
                    (paraProductsPagination.current - 1) * paraProductsPagination.pageSize,
                    paraProductsPagination.current * paraProductsPagination.pageSize
                  );
                })()}
                columns={[
                  {
                    title: "Product Name",
                    dataIndex: "name",
                    key: "name",
                    sorter: true,
                    sortOrder: paraProductsSortConfig.field === 'name' ? paraProductsSortConfig.direction : null,
                    sortDirections: ['ascend', 'descend'],
                    showSorterTooltip: false,
                  }
                ]}
                pagination={false}
                rowKey="id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setParaProductsSortConfig, setParaProductsPagination)
                }
              />
              <CustomPagination
                current={paraProductsPagination.current}
                total={selectedGlobalParaProducts.length}
                pageSize={paraProductsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setParaProductsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No para products selected yet"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title={
            <div className="card-title-with-edit">
              <span>Para Product Mapping</span>
              <div>
                <Tooltip title="Add New">
                <Button 
                  type="link" 
                  icon={<PlusOutlined />} 
                  onClick={() => {
                    setIsAddingParaMapping(true);
                    setSelectedProductForParaMapping(null);
                    setSelectedParaProductName("");
                    setShowParaMappingModal(true);
                  }}
                  style={{ marginRight: 8 }}
                />
                </Tooltip>
              </div>
            </div>
          }
          className="company-products-card"
          loading={loading}
        >
          {paraProductMappings?.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(paraProductMappings, paraMappingsSortConfig);
                  return sortedData.slice(
                    (paraMappingsPagination.current - 1) * paraMappingsPagination.pageSize,
                    paraMappingsPagination.current * paraMappingsPagination.pageSize
                  );
                })()}
                columns={columnsParaProductMapping}
                pagination={false}
                rowKey="para_name"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setParaMappingsSortConfig, setParaMappingsPagination)
                }
              />
              <CustomPagination
                current={paraMappingsPagination.current}
                total={paraProductMappings.length}
                pageSize={paraMappingsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setParaMappingsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No data available"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title="ERP Approved" 
          className="company-products-card"
          loading={loading}
        >
          {erpApproved.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(erpApproved, erpApprovedSortConfig);
                  return sortedData.slice(
                    (erpApprovedPagination.current - 1) * erpApprovedPagination.pageSize,
                    erpApprovedPagination.current * erpApprovedPagination.pageSize
                  );
                })()}
                columns={columnsERPApproved}
                pagination={false}
                rowKey="key"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setErpApprovedSortConfig, setErpApprovedPagination)
                }
              />
              <CustomPagination
                current={erpApprovedPagination.current}
                total={erpApproved.length}
                pageSize={erpApprovedPagination.pageSize}
                onChange={(page, pageSize) => {
                  setErpApprovedPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No data available"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>

      <Col span={12}>
        <Card 
          title="Display Active Product on Website" 
          className="company-products-card"
          loading={loading}
        >
          {ActiveProducts?.length > 0 ? (
            <>
              <Table
                dataSource={(() => {
                  const sortedData = getSortedData(ActiveProducts, displayProductsSortConfig);
                  return sortedData.slice(
                    (displayProductsPagination.current - 1) * displayProductsPagination.pageSize,
                    displayProductsPagination.current * displayProductsPagination.pageSize
                  );
                })()}
                columns={columnsDisplayProducts}
                pagination={false}
                rowKey="active_product_id"
                scroll={{ y: 250 }}
                className="company-products-table"
                onChange={(pagination, filters, sorter) => 
                  handleTableChange(pagination, filters, sorter, setDisplayProductsSortConfig, setDisplayProductsPagination)
                }
              />
              <CustomPagination
                current={displayProductsPagination.current}
                total={ActiveProducts.length}
                pageSize={displayProductsPagination.pageSize}
                onChange={(page, pageSize) => {
                  setDisplayProductsPagination({
                    current: page,
                    pageSize: pageSize,
                  });
                }}
              />
            </>
          ) : (
            <Empty 
              image={Empty.PRESENTED_IMAGE_SIMPLE} 
              description="No data available"
              style={{ margin: '40px 0' }}
            />
          )}
        </Card>
      </Col>
    </Row>
    
    {/* ERP Approval Modal */}
    <SingleSelectDropdownModal
      visible={erpModalVisible}
      onCancel={() => setErpModalVisible(false)}
      onSave={() => handleERPStatusUpdate(selectedProduct?.is_erp_approved)}
      title="Update ERP Approval Status"
      label="ERP Approval Status"
      value={selectedProduct?.is_erp_approved}
      options={[
        { value: true, label: "Approved" },
        { value: false, label: "Not Approved" },
      ]}
      onChange={(value) => setSelectedProduct(prev => ({
        ...prev,
        is_erp_approved: value
      }))}
      disabledInput={true}
      inputValue={selectedProduct?.name || ""}
      inputLabel="Product Name"
    />

    {/* Display on Website Modal */}
    <SingleSelectDropdownModal
      visible={displayModalVisible}
      onCancel={() => setDisplayModalVisible(false)}
      onSave={() => handleDisplayStatusUpdate(selectedProduct?.display_on_website)}
      title="Update Display on Website Status"
      label="Display on Website"
      value={selectedProduct?.display_on_website}
      options={[
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ]}
      onChange={(value) => setSelectedProduct(prev => ({
        ...prev,
        display_on_website: value
      }))}
      disabledInput={true}
      inputValue={selectedProduct?.name || ""}
      inputLabel="Product Name"
      secondaryInputValue={selectedProduct?.company}
      secondaryInputLabel="Company Name"
    />

    {/* Para Product Mapping Modal */}
    <SingleSelectDropdownModal
      visible={showParaMappingModal}
      onCancel={() => {
        setShowParaMappingModal(false);
        setIsAddingParaMapping(false);
      }}
      onSave={isAddingParaMapping ? handleSaveNewParaMapping : handleSaveParaMapping}
      title={isAddingParaMapping ? "Add New Para Product Mapping" : "Update Para Product Mapping"}
      label="Select Para Product"
      value={isAddingParaMapping ? selectedParaProductName : selectedParaProduct?.para_name}
      options={allCompanyProducts.map(product => ({
        value: product.name,
        label: product.name
      }))}
      onChange={(value) => {
        if (isAddingParaMapping) {
          setSelectedParaProductName(value);
        } else {
          setSelectedParaProduct(prev => ({
            ...prev,
            para_name: value
          }));
        }
      }}
      disabledInput={!isAddingParaMapping}
      inputValue={isAddingParaMapping ? (selectedProductForParaMapping?.name || "") : (selectedMappedProduct?.name || "")}
      inputLabel="Product Name"
      showProductSelect={isAddingParaMapping}
      allProducts={allCompanyProducts}
      selectedProduct={selectedProductForParaMapping}
      setSelectedProduct={setSelectedProductForParaMapping}
    />

    <EditPanelModal
      isOpen={showCountriesPanel}
      onClose={() => setShowCountriesPanel(false)}
      onSave={handleSaveCountries}
      title="Exclude High Income Countries"
      items={countryList}
      selectedItems={selectedCountries}
      itemRenderer={countryRenderer}
      loading={saving}
    />

    <EditPanelModal
      isOpen={showProductsPanel}
      onClose={() => setShowProductsPanel(false)}
      onSave={handleSaveProducts}
      title="Exclude Products For All Companies"
      items={allCompanyProducts}
      selectedItems={selectedGlobalProducts}
      itemRenderer={productRenderer}
      loading={saving}
    />

    <EditPanelModal
      isOpen={showParaProductsPanel}
      onClose={() => setShowParaProductsPanel(false)}
      onSave={handleSaveParaProducts}
      title="Exclude Para Products For All Companies"
      items={allCompanyProducts}
      selectedItems={selectedGlobalParaProducts}
      itemRenderer={productRenderer}
      loading={saving}
    />

    <MultiSelectModal
      visible={showCompanyExclusionsModal}
      onCancel={() => {
        setShowCompanyExclusionsModal(false);
        setIsAddingCompanyExclusion(false);
      }}
      onSave={isAddingCompanyExclusion ? handleAddCompanyExclusion : handleSaveCompanyExclusions}
      title={isAddingCompanyExclusion ? "Add New Exclude Product for Specific Company" : "Edit Exclude Product for Specific Company"}
      loading={fetchingPartners || saving}
      productName={isAddingCompanyExclusion ? (selectedProductForCompanyAdd?.name || "") : selectedProduct?.product_name}
      options={productPartners}
      selectedValues={selectedCompanies}
      setSelectedValues={setSelectedCompanies}
      productLabel="Product:"
      selectLabel="Select Companies to Exclude:"
      placeholder="Select companies"
      showProductSelect={isAddingCompanyExclusion}
      allProducts={allCompanyProducts}
      selectedProduct={selectedProductForCompanyAdd}
      setSelectedProduct={setSelectedProductForCompanyAdd}
      onProductChange={isAddingCompanyExclusion ? handleProductChangeForCompanyAdd : undefined}
    />

    <MultiSelectModal
      visible={showCountryExclusionsModal}
      onCancel={() => {
        setShowCountryExclusionsModal(false);
        setIsAddingCountryExclusion(false);
      }}
      onSave={isAddingCountryExclusion ? handleAddCountryExclusion : handleSaveCountryExclusions}
      title={isAddingCountryExclusion ? "Add New Exclude Product for Specific Country" : "Edit Exclude Product for Specific Country"}
      loading={fetchingCountries || saving}
      productName={isAddingCountryExclusion ? (selectedProductForAdd?.name || "") : selectedProduct?.product_name}
      options={countryList.map(country => country.name)}
      selectedValues={selectedCountryExclusions.map(iso => {
        const country = countryList.find(c => c.iso === iso);
        return country ? country.name : iso;
      })}
      setSelectedValues={(names) => {
        const isos = names.map(name => {
          const country = countryList.find(c => c.name === name);
          return country ? country.iso : name;
        });
        setSelectedCountryExclusions(isos);
      }}
      productLabel="Product:"
      selectLabel="Select Countries to Exclude:"
      placeholder="Select countries"
      optionsAreCountries={true}
      countryList={countryList}
      showProductSelect={isAddingCountryExclusion}
      allProducts={allCompanyProducts}
      selectedProduct={selectedProductForAdd}
      setSelectedProduct={setSelectedProductForAdd}
    />
    <ConfirmationPopup
      visible={deleteConfirmationVisible}
      title="CONFIRM DELETE"
      message={`Are you sure you want to delete this ${deleteType} exclusion? This action cannot be undone.`}
      confirmText="DELETE"
      cancelText="CANCEL"
      confirmButtonType="danger"
      onConfirm={handleConfirmDelete}
      onCancel={handleCancelDelete}
    />
  </div>
  );
};

export default DataManagement;